import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { modelService, uploadService, batchInferenceService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { EnhancedStepMessage } from '../components/ui/CodeBlock';
import ShareModelButton from '../components/models/ShareModelButton';
import BuildProgress from '../components/models/BuildProgress';
import { DropdownMenu, DropdownItem, DropdownDivider } from '../components/ui/DropdownMenu';
import CheckpointInspector from '../components/models/CheckpointInspector';
import {
  ArrowLeftIcon,
  ClipboardIcon,
  Code2Icon as CodeIcon,
  InfoIcon,
  BarChartIcon,
  DatabaseIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  RefreshCwIcon as ArrowPathIcon,
  RefreshCwIcon,
  ActivityIcon,
  PackageIcon,
  FlaskConicalIcon,
  ZapIcon,
  AlertTriangleIcon,
  FilesIcon,
  CpuIcon,
  LayersIcon,
  PlusIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  DownloadIcon,
  ListIcon,
  GitBranchIcon,
  HistoryIcon,
  SparklesIcon,
  ExternalLinkIcon,
} from 'lucide-react';
import { buildOpenApiSpec, buildScalarHtml, downloadFile, slugify } from '../utils/openApiGenerator';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Editor from '@monaco-editor/react';

// Safe render utility to prevent React object rendering errors
const safeRender = (val) => {
  if (val === null || val === undefined) return '-';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

// Custom hook for model polling and retry logic
function useModelPolling(modelId) {
  const [model, setModel] = useState(null);
  const [modelDescription, setModelDescription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingRetryCount, setLoadingRetryCount] = useState(0);
  const { notify } = useNotification();
  
  // Function to fetch model details
  const fetchModelDetails = useCallback(async () => {
    try {
      setLoading(true);
      const modelData = await modelService.getModel(modelId);
      
      // Bail early if modelId no longer valid (component unmounted)
      if (!modelId) return;
      
      if (modelData) {
        setModel(modelData);
        
        // Reset retry count for completed models
        if (modelData.status === 'completed') {
          setLoadingRetryCount(0);
          
          // If model is completed, fetch the model description
          try {
            let modelName, modelVersion;
            if (modelId.includes('/')) {
              [modelName, modelVersion] = modelId.split('/');
            } else {
              modelName = "model";
              modelVersion = modelId;
            }
            
            const descriptionData = await modelService.getModelDescription(modelName, modelVersion);
            if (descriptionData) {
              setModelDescription(descriptionData);
            }
          } catch (descError) {
            console.error('Failed to load model description:', descError);
          }
        }
      }
      
      setLoading(false);
    } catch (error) {
      notify.error('Failed to load model details');
      // Don't set loading to false here, let retry mechanism handle it
    }
  }, [modelId, notify]);

  // Set up polling for processing models
  useEffect(() => {
    let intervalId = null;
    
    const startPolling = async () => {
      // Only start polling if model is processing, pending, or baseline_deployed
      if (!model || (model.status !== 'processing' && model.status !== 'pending' && model.status !== 'baseline_deployed')) {
        return;
      }
      
      intervalId = setInterval(async () => {
        try {
          const updatedModel = await modelService.getModel(modelId);
          
          // Bail if component unmounted
          if (!modelId) {
            clearInterval(intervalId);
            return;
          }
          
          setModel(updatedModel);
          
          // If model is no longer processing or baseline_deployed, stop polling
          if (updatedModel.status !== 'processing' && updatedModel.status !== 'pending' && updatedModel.status !== 'baseline_deployed') {
            clearInterval(intervalId);
            
            // Notify user of failure
            if (updatedModel.status === 'failed') {
              notify.error('Model training failed');
            }
          }
        } catch (error) {
          clearInterval(intervalId);
        }
      }, 5000); // Poll every 5 seconds
    };
    
    // Start polling if we have a model in processing, pending, or baseline_deployed state
    if (model && (model.status === 'processing' || model.status === 'pending' || model.status === 'baseline_deployed')) {
      startPolling();
    }
    
    // Clean up interval on unmount
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [model, modelId, notify]);

  // Initial data fetch
  useEffect(() => {
    fetchModelDetails();
  }, [fetchModelDetails]);

  // Force exit from loading state after 3 retries and create placeholder model
  useEffect(() => {
    if (loading && loadingRetryCount >= 3) {
      setLoading(false);
      
      // Create placeholder model if needed
      if (modelId && !model) {
        setModel({
          model_id: modelId,
          model_name: "Model " + modelId.substring(0, 8),
          status: "processing",
          created_at: new Date().toISOString(),
          intent: "Model is being trained",
          provider: "Loading...",
          input_schema: { properties: {} },
          output_schema: { properties: {} }
        });
      }
    }
  }, [loading, loadingRetryCount, modelId, model]);

  // Increment retry counter while loading
  useEffect(() => {
    let retryTimer;
    
    if (loading && (!model || (model && model.status !== 'completed'))) {
      retryTimer = setTimeout(() => {
        setLoadingRetryCount(prev => prev + 1);
        
        // Try to fetch model data again on second retry
        if (loadingRetryCount === 1) {
          modelService.getModel(modelId)
            .then(modelData => {
              if (modelData) {
                setModel(modelData);
              }
              setLoading(false);
            })
            .catch(() => {
              // Continue with retry mechanism
            });
        }
      }, 1000); // Try every second
    }
    
    return () => {
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [loading, loadingRetryCount, modelId, model]);

  return {
    model,
    modelDescription,
    loading,
    loadingRetryCount,
    fetchModelDetails
  };
}

// Component for rendering model schema sections
function ModelSchemas({ model, modelDescription, copyToClipboard, copiedText }) {
  // Use modelDescription data if available, fallback to model data
  const inputSchema = modelDescription?.input_schema || model.input_schema;
  const outputSchema = modelDescription?.output_schema || model.output_schema;
  
  if (!inputSchema && !outputSchema) return null;

  // Helper function to render schema (handles both object and dict formats)
  const renderSchema = (schema) => {
    if (!schema) return null;

    // Helper to safely render type information
    const renderType = (typeInfo) => {
      if (typeof typeInfo === 'string') {
        return typeInfo;
      }
      if (typeof typeInfo === 'object' && typeInfo !== null) {
        // If it's an object, try to extract the type field or stringify it
        if (typeInfo.type) {
          return typeInfo.type;
        }
        return JSON.stringify(typeInfo);
      }
      return String(typeInfo);
    };

    // If it's a simple key-value object (new format)
    if (!schema.properties && typeof schema === 'object') {
      return Object.entries(schema).map(([key, type]) => {
        const typeStr = renderType(type);
        const description = typeof type === 'object' && type?.description ? type.description : null;
        const isRequired = typeof type === 'object' && type?.required ? type.required : false;

        return (
          <div key={key} className="py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
            <span className="font-mono text-sm text-primary-600 dark:text-primary-400">{key}</span>
            {isRequired && (
              <span className="text-xs text-red-500 dark:text-red-400 ml-1">*</span>
            )}
            <span className="text-gray-500 dark:text-gray-400 mx-2">:</span>
            <span className="text-sm text-gray-700 dark:text-gray-300">{typeStr}</span>
            {description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-4">{description}</p>
            )}
          </div>
        );
      });
    }

    // Original properties-based format
    if (schema.properties) {
      return Object.entries(schema.properties).map(([key, value]) => {
        const typeStr = renderType(value.type || value);

        return (
          <div key={key} className="py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
            <span className="font-mono text-sm text-primary-600 dark:text-primary-400">{key}</span>
            <span className="text-gray-500 dark:text-gray-400 mx-2">:</span>
            <span className="text-sm text-gray-700 dark:text-gray-300">{typeStr}</span>
            {value.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-4">{value.description}</p>
            )}
          </div>
        );
      });
    }

    return null;
  };
  
  return (
    <div className="space-y-6">
      {/* Input Schema */}
      {inputSchema && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            Input Schema
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            Expected input format for this model.
          </p>
          <div className="relative">
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-md p-4">
              {renderSchema(inputSchema)}
            </div>
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-300 transition-all duration-200 transform hover:scale-110"
              onClick={() => copyToClipboard(JSON.stringify(inputSchema || {}, null, 2))}
            >
              {copiedText === JSON.stringify(inputSchema || {}, null, 2) ? (
                <CheckCircleIcon className="h-4 w-4 text-green-500" />
              ) : (
                <ClipboardIcon className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Output Schema */}
      {outputSchema && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            Output Schema
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            Expected output format from this model.
          </p>
          <div className="relative">
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-md p-4">
              {renderSchema(outputSchema)}
            </div>
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-300 transition-all duration-200 transform hover:scale-110"
              onClick={() => copyToClipboard(JSON.stringify(outputSchema || {}, null, 2))}
            >
              {copiedText === JSON.stringify(outputSchema || {}, null, 2) ? (
                <CheckCircleIcon className="h-4 w-4 text-green-500" />
              ) : (
                <ClipboardIcon className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// Helper component to render nested dictionaries flexibly
function FlexibleDictRenderer({ data, depth = 0 }) {
  // Handle non-object primitives
  if (data === null || data === undefined) {
    return <span className="text-sm text-gray-700 dark:text-gray-300">-</span>;
  }

  if (typeof data !== 'object') {
    return <span className="text-sm text-gray-700 dark:text-gray-300">{String(data)}</span>;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    const stringifiedArray = data.map((item) => {
      if (typeof item === 'object' && item !== null) {
        return JSON.stringify(item);
      }
      return String(item);
    }).join(', ');

    return (
      <span className="text-sm text-gray-700 dark:text-gray-300">
        {stringifiedArray}
      </span>
    );
  }

  const entries = Object.entries(data);

  return (
    <div className={`${depth > 0 ? 'ml-4' : ''} space-y-2`}>
      {entries.map(([key, value]) => {
        const isObject = value && typeof value === 'object' && !Array.isArray(value);
        const isArray = Array.isArray(value);

        return (
          <div key={key} className="text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {key.replace(/_/g, ' ')}:
            </span>
            {isObject ? (
              <div className="mt-1">
                <FlexibleDictRenderer data={value} depth={depth + 1} />
              </div>
            ) : isArray ? (
              <div className="mt-1">
                <FlexibleDictRenderer data={value} depth={depth + 1} />
              </div>
            ) : (
              <span className="ml-2 text-gray-600 dark:text-gray-400">
                {typeof value === 'number' ? value.toFixed(3) : value === null || value === undefined ? '-' : String(value)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Priority badge component
function PriorityBadge({ priority }) {
  const styles = {
    HIGH: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    MEDIUM: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    LOW: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[priority] || styles.LOW}`}>
      {priority}
    </span>
  );
}

// Executive Summary Component
function EvaluationExecutiveSummary({ evaluation }) {
  if (!evaluation) return null;

  const getVerdictColor = (verdict) => {
    if (verdict === 'PASS') return '#10b981';
    if (verdict === 'FAIL') return '#ef4444';
    return '#f59e0b';
  };

  const getVerdictBgColor = (verdict) => {
    if (verdict === 'PASS') return 'bg-emerald-50 dark:bg-emerald-900/10';
    if (verdict === 'FAIL') return 'bg-red-50 dark:bg-red-900/10';
    return 'bg-amber-50 dark:bg-amber-900/10';
  };

  const getVerdictBorderColor = (verdict) => {
    if (verdict === 'PASS') return 'border-emerald-200 dark:border-emerald-800';
    if (verdict === 'FAIL') return 'border-red-200 dark:border-red-800';
    return 'border-amber-200 dark:border-amber-800';
  };

  return (
    <div className="mb-12">
      {/* Header with minimal styling */}
      <div className={`border-b pb-6 mb-6 ${getVerdictBorderColor(evaluation.verdict)}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-light text-gray-900 dark:text-gray-100 mb-2">
              Model Evaluation Report
            </h3>
            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
              <span>Status: <span className={`font-medium px-2 py-0.5 rounded ${getVerdictBgColor(evaluation.verdict)}`} style={{ color: getVerdictColor(evaluation.verdict) }}>
                {evaluation.verdict ? evaluation.verdict.replace(/_/g, ' ') : 'N/A'}
              </span></span>
              {evaluation.deployment_ready !== undefined && (
                <span className="flex items-center">
                  <span className={`w-2 h-2 rounded-full mr-2 ${evaluation.deployment_ready ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                  {evaluation.deployment_ready ? 'Production Ready' : 'Development Stage'}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary text with clean typography */}
      {evaluation.summary && (
        <div className="prose dark:prose-invert max-w-none mb-8">
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-base">
            {evaluation.summary}
          </p>
        </div>
      )}

      {/* Key Concerns - minimal card design */}
      {evaluation.key_concerns && evaluation.key_concerns.length > 0 && (
        <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-4">
            Key Concerns
          </h4>
          <div className="space-y-3">
            {evaluation.key_concerns.map((concern, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-1 h-1 rounded-full bg-gray-400 dark:bg-gray-500 mt-2"></div>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {safeRender(concern)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Core Metrics Component
function CoreMetricsSection({ coreMetrics }) {
  if (!coreMetrics) return null;

  // Keep metrics in original order (don't sort)
  const metricsEntries = coreMetrics.all_metrics
    ? Object.entries(coreMetrics.all_metrics)
    : [];

  // Helper to parse values (handle both strings and numbers)
  const parseValue = (val) => {
    if (typeof val === 'string') {
      return parseFloat(val);
    }
    return val;
  };

  // Format primary metric value
  const primaryValue = coreMetrics.primary_metric_value != null ? parseValue(coreMetrics.primary_metric_value) : null;
  const primaryCiLower = coreMetrics.primary_metric_ci_lower ? parseValue(coreMetrics.primary_metric_ci_lower) : null;
  const primaryCiUpper = coreMetrics.primary_metric_ci_upper ? parseValue(coreMetrics.primary_metric_ci_upper) : null;

  return (
    <div className="mb-12">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-6">
        Performance Metrics
      </h3>

      {/* Primary Metric - Clean Card */}
      <div className="mb-8 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50">
          <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {coreMetrics.primary_metric_name || 'Primary Metric'}
          </h4>
        </div>
        <div className="px-6 py-8 text-center">
          <div className="text-5xl font-light mb-2 text-gray-900 dark:text-gray-100">
            {primaryValue !== null && !isNaN(primaryValue)
              ? primaryValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              : '—'
            }
          </div>
          {primaryCiLower !== null && primaryCiUpper !== null && (
            <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">
              95% CI [{primaryCiLower.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}, {primaryCiUpper.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}]
            </p>
          )}
        </div>
      </div>

      {/* All Metrics - Table Format */}
      {metricsEntries.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-slate-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Metric
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  95% CI
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {metricsEntries.map(([metric, value]) => {
                const ci = coreMetrics.metric_confidence_intervals?.[metric];
                const numValue = parseValue(value);

                return (
                  <tr key={metric} className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 capitalize">
                      {metric.replace(/_/g, ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-mono text-gray-900 dark:text-gray-100">
                      {numValue !== null && numValue !== undefined && !isNaN(numValue)
                        ? numValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '—'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-mono text-gray-500 dark:text-gray-400">
                      {ci && Array.isArray(ci) && ci.length === 2 ? (
                        `[${parseValue(ci[0]).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}, ${parseValue(ci[1]).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}]`
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Statistical Notes - Collapsible */}
      {coreMetrics.statistical_notes && (
        <details className="group">
          <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 flex items-center">
            <ChevronRightIcon className="w-4 h-4 mr-1 group-open:rotate-90 transition-transform" />
            Statistical Analysis Notes
          </summary>
          <div className="mt-3 pl-5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap border-l-2 border-gray-200 dark:border-gray-700 ml-2">
            {coreMetrics.statistical_notes}
          </div>
        </details>
      )}
    </div>
  );
}

// Diagnostics Section Component
function DiagnosticsSection({ diagnostics }) {
  const [showAllPredictions, setShowAllPredictions] = useState(false);

  if (!diagnostics) return null;

  const displayedPredictions = showAllPredictions
    ? diagnostics.worst_predictions
    : diagnostics.worst_predictions?.slice(0, 5);

  return (
    <div className="mb-12">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-6">
        Model Diagnostics
      </h3>

      <div className="space-y-6">
        {/* Error Patterns & Key Insights - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {diagnostics.error_patterns && diagnostics.error_patterns.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-4">
                Error Patterns
              </h4>
              <div className="space-y-3">
                {diagnostics.error_patterns.map((pattern, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 mt-2"></div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      {safeRender(pattern)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {diagnostics.key_insights && diagnostics.key_insights.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-4">
                Key Insights
              </h4>
              <div className="space-y-3">
                {diagnostics.key_insights.map((insight, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 mt-2"></div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      {safeRender(insight)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Subgroup Analysis */}
        {diagnostics.subgroup_analysis && (
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Subgroup Analysis
              </h4>
            </div>
            <div className="p-6 space-y-6">
              {Object.entries(diagnostics.subgroup_analysis).map(([groupName, subgroups]) => (
                <div key={groupName}>
                  <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 capitalize">
                    {groupName.replace(/_/g, ' ')}
                  </h5>
                  <FlexibleDictRenderer data={subgroups} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error Distribution Summary */}
        {diagnostics.error_distribution_summary && (
          <details className="group bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <summary className="cursor-pointer px-6 py-4 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 flex items-center">
              <ChevronRightIcon className="w-4 h-4 mr-2 group-open:rotate-90 transition-transform" />
              Error Distribution Analysis
            </summary>
            <div className="px-6 pb-6 text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap border-t border-gray-200 dark:border-gray-700 pt-4">
              {diagnostics.error_distribution_summary}
            </div>
          </details>
        )}

        {/* Worst Predictions */}
        {diagnostics.worst_predictions && diagnostics.worst_predictions.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Worst Predictions
              </h4>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {diagnostics.worst_predictions.length} samples
              </span>
            </div>
            <div className="p-6 space-y-3">
              {displayedPredictions.map((pred, idx) => (
                <details key={idx} className="group border border-gray-200 dark:border-gray-700 rounded">
                  <summary className="cursor-pointer px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-xs font-mono text-gray-500 dark:text-gray-400">#{pred.index}</span>
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {pred.error_type || 'Error'}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          True: {safeRender(pred.true_value)} → Pred: {safeRender(pred.predicted_value)}
                        </span>
                      </div>
                      <span className="text-xs font-mono text-gray-600 dark:text-gray-400">
                        Δ {safeRender(pred.error)}
                      </span>
                    </div>
                  </summary>
                  <div className="px-4 pb-4 pt-2 bg-gray-50 dark:bg-slate-700/30 border-t border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">Features</p>
                    <FlexibleDictRenderer data={pred.features} />
                  </div>
                </details>
              ))}

              {diagnostics.worst_predictions.length > 5 && (
                <button
                  onClick={() => setShowAllPredictions(!showAllPredictions)}
                  className="w-full mt-2 py-2 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 flex items-center justify-center border-t border-gray-200 dark:border-gray-700"
                >
                  {showAllPredictions ? (
                    <>
                      <ChevronDownIcon className="w-3 h-3 mr-1 rotate-180" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDownIcon className="w-3 h-3 mr-1" />
                      Show {diagnostics.worst_predictions.length - 5} More
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Robustness Section Component
function RobustnessSection({ robustness }) {
  if (!robustness) return null;

  const getGradeColor = (grade) => {
    if (grade === 'A') return 'text-emerald-600 dark:text-emerald-400';
    if (grade === 'B') return 'text-blue-600 dark:text-blue-400';
    if (grade === 'C') return 'text-amber-600 dark:text-amber-400';
    if (grade === 'D') return 'text-orange-600 dark:text-orange-400';
    if (grade === 'F') return 'text-rose-600 dark:text-rose-400';
    return 'text-gray-900 dark:text-gray-100';
  };

  return (
    <div className="mb-12">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-6">
        Robustness Assessment
      </h3>

      {/* Grade and Consistency Score - Clean Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-4">
            Robustness Grade
          </div>
          <div className="text-center">
            <div className={`text-5xl font-light ${getGradeColor(robustness.robustness_grade)}`}>
              {robustness.robustness_grade || '—'}
            </div>
          </div>
        </div>
        {robustness.consistency_score !== null && robustness.consistency_score !== undefined && (
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-4">
              Consistency Score
            </div>
            <div className="text-center">
              <div className={`text-5xl font-light ${
                (robustness.consistency_score * 100) >= 90
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : (robustness.consistency_score * 100) >= 75
                  ? 'text-blue-600 dark:text-blue-400'
                  : (robustness.consistency_score * 100) >= 60
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-gray-900 dark:text-gray-100'
              }`}>
                {(robustness.consistency_score * 100).toFixed(1)}<span className="text-2xl text-gray-500">%</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Perturbation Tests */}
      {robustness.perturbation_tests && Object.keys(robustness.perturbation_tests).length > 0 && (
        <div className="mb-6">
          <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-4">
            Perturbation Tests
          </h4>
          <div className="space-y-3">
            {Object.entries(robustness.perturbation_tests).map(([testName, testData]) => (
              <details key={testName} className="group bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <summary className="cursor-pointer px-6 py-4 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors flex items-center capitalize">
                  <ChevronRightIcon className="w-4 h-4 mr-2 group-open:rotate-90 transition-transform text-gray-400" />
                  {testName.replace(/_/g, ' ')}
                </summary>
                <div className="px-6 pb-6 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <FlexibleDictRenderer data={testData} />
                </div>
              </details>
            ))}
          </div>
        </div>
      )}

      {/* Concerns & Recommendations - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {robustness.concerns && robustness.concerns.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-4">
              Concerns
            </h4>
            <div className="space-y-3">
              {robustness.concerns.map((concern, idx) => (
                <div key={idx} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 mt-2"></div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    {safeRender(concern)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {robustness.recommendations && robustness.recommendations.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-4">
              Recommendations
            </h4>
            <div className="space-y-3">
              {robustness.recommendations.map((rec, idx) => (
                <div key={idx} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 mt-2"></div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    {safeRender(rec)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Explainability Section Component
function ExplainabilitySection({ explainability }) {
  if (!explainability) return null;

  // Sort features by importance
  const sortedFeatures = explainability.feature_importance
    ? Object.entries(explainability.feature_importance)
        .sort(([, a], [, b]) => Math.abs(b) - Math.abs(a))
        .slice(0, 15) // Top 15
    : [];

  return (
    <div className="mb-12">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
          Feature Importance Analysis
        </h3>
        {explainability.method_used && (
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Method: {explainability.method_used}
          </span>
        )}
      </div>

      {/* Feature Importance - Clean Chart */}
      {sortedFeatures.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50">
            <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              Feature Contributions
            </h4>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {sortedFeatures.map(([feature, importance], idx) => {
                const numImportance = typeof importance === 'number' ? importance : parseFloat(importance);
                const absImportance = Math.abs(numImportance);
                const maxImportance = Math.abs(typeof sortedFeatures[0][1] === 'number' ? sortedFeatures[0][1] : parseFloat(sortedFeatures[0][1]));
                const width = (absImportance / maxImportance) * 100;
                const isPositive = numImportance >= 0;

                return (
                  <div key={feature} className="space-y-1">
                    <div className="flex items-baseline justify-between">
                      <div className="flex items-baseline space-x-2">
                        <span className="text-xs font-mono text-gray-400 dark:text-gray-500">
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        <span className="text-sm text-gray-900 dark:text-gray-100" title={feature}>
                          {feature}
                        </span>
                      </div>
                      <span className={`text-xs font-mono ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                        {!isNaN(numImportance) ? `${numImportance > 0 ? '+' : ''}${numImportance.toFixed(4)}` : '—'}
                      </span>
                    </div>
                    <div className="relative h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`absolute left-0 top-0 h-full rounded-full ${isPositive ? 'bg-emerald-500 dark:bg-emerald-600' : 'bg-rose-500 dark:bg-rose-600'}`}
                        style={{ width: `${!isNaN(width) ? width : 0}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Top Features Summary */}
      {explainability.top_features && explainability.top_features.length > 0 && (
        <div className="mb-6">
          <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-3">
            Most Influential Features
          </h4>
          <div className="flex flex-wrap gap-2">
            {explainability.top_features.map((feature, idx) => (
              <span key={idx} className="inline-flex items-center px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded border border-gray-200 dark:border-gray-600">
                <span className="font-mono text-gray-400 dark:text-gray-500 mr-1.5">{idx + 1}.</span>
                {safeRender(feature)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Interpretation */}
      {explainability.interpretation && (
        <details className="group">
          <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 flex items-center">
            <ChevronRightIcon className="w-4 h-4 mr-1 group-open:rotate-90 transition-transform" />
            Interpretation Notes
          </summary>
          <div className="mt-3 pl-5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap border-l-2 border-gray-200 dark:border-gray-700 ml-2">
            {explainability.interpretation}
          </div>
        </details>
      )}
    </div>
  );
}

// Baseline Comparison Section Component
function BaselineComparisonSection({ baselineComparison }) {
  if (!baselineComparison) return null;

  // Helper to parse values
  const parseValue = (val) => {
    if (typeof val === 'string') return parseFloat(val);
    return val;
  };

  // Get all metrics from baseline performance
  const metricsEntries = baselineComparison.baseline_performance
    ? Object.entries(baselineComparison.baseline_performance)
    : [];

  // Get baseline type badge styling
  const getTypeBadge = (type) => {
    const styles = {
      heuristic: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      statistical: 'bg-purple-100 text-purple-800 dark:bg-slate-800/50 dark:text-silver-400',
      simple_model: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
    };
    return styles[type] || styles.heuristic;
  };

  // Get delta styling (positive/negative indicator)
  const getDeltaStyle = (delta) => {
    if (delta > 0) return 'text-emerald-600 dark:text-emerald-400';
    if (delta < 0) return 'text-rose-600 dark:text-rose-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  return (
    <div className="mb-12">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-6">
        Baseline Comparison
      </h3>

      {/* Baseline Info Card */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {baselineComparison.baseline_name || 'Baseline'}
            </h4>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadge(baselineComparison.baseline_type)}`}>
              {baselineComparison.baseline_type?.replace(/_/g, ' ') || 'N/A'}
            </span>
          </div>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          {baselineComparison.baseline_description || 'No description available'}
        </p>
      </div>

      {/* Performance Comparison Table */}
      {metricsEntries.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50">
            <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              Performance Comparison
            </h4>
          </div>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-slate-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Metric
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Baseline
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Δ Absolute
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Δ %
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {metricsEntries.map(([metric, baselineValue]) => {
                const modelValue = baselineComparison.model_performance?.[metric];
                const delta = baselineComparison.performance_delta?.[metric];
                const deltaPct = baselineComparison.performance_delta_pct?.[metric];

                return (
                  <tr key={metric} className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 capitalize">
                      {metric.replace(/_/g, ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-mono text-gray-600 dark:text-gray-400">
                      {baselineValue != null ? parseValue(baselineValue).toFixed(3) : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-mono text-gray-900 dark:text-gray-100">
                      {modelValue != null ? parseValue(modelValue).toFixed(3) : '—'}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-mono ${getDeltaStyle(delta)}`}>
                      {delta != null ? (delta > 0 ? '+' : '') + parseValue(delta).toFixed(3) : '—'}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-mono ${getDeltaStyle(deltaPct)}`}>
                      {deltaPct != null ? (deltaPct > 0 ? '+' : '') + parseValue(deltaPct).toFixed(1) + '%' : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Interpretation */}
      {baselineComparison.interpretation && (
        <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-3">
            Analysis
          </h4>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
            {baselineComparison.interpretation}
          </p>
        </div>
      )}
    </div>
  );
}

// Recommendations Section Component
function RecommendationsSection({ recommendations }) {
  if (!recommendations || recommendations.length === 0) return null;

  const groupedRecs = {
    HIGH: recommendations.filter(r => r.priority === 'HIGH'),
    MEDIUM: recommendations.filter(r => r.priority === 'MEDIUM'),
    LOW: recommendations.filter(r => r.priority === 'LOW')
  };

  const priorityLabels = {
    HIGH: 'High Priority',
    MEDIUM: 'Medium Priority',
    LOW: 'Low Priority'
  };

  return (
    <div className="mb-12">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-6">
        Recommendations
      </h3>

      <div className="space-y-6">
        {Object.entries(groupedRecs).map(([priority, recs]) => {
          if (recs.length === 0) return null;

          return (
            <div key={priority} className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50 flex items-center justify-between">
                <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {priorityLabels[priority]}
                </h4>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {recs.length} {recs.length === 1 ? 'item' : 'items'}
                </span>
              </div>
              <div className="p-6 space-y-4">
                {recs.map((rec, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-start space-x-3">
                      <span className="flex-shrink-0 text-xs font-mono text-gray-400 dark:text-gray-500 mt-0.5">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {safeRender(rec.action)}
                        </p>
                        {rec.rationale && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                            {safeRender(rec.rationale)}
                          </p>
                        )}
                      </div>
                    </div>
                    {idx < recs.length - 1 && (
                      <div className="ml-8 border-b border-gray-100 dark:border-gray-700"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Component for rendering model performance section
function PerformanceSection({ modelDescription }) {
  return (
    <div>
      {/* Evaluation Section */}
      {modelDescription?.evaluation ? (
        <div>
          {/* Executive Summary */}
          <EvaluationExecutiveSummary evaluation={modelDescription.evaluation} />

          {/* Core Metrics */}
          <CoreMetricsSection coreMetrics={modelDescription.evaluation.core_metrics} />

          {/* Baseline Comparison */}
          <BaselineComparisonSection baselineComparison={modelDescription.evaluation.baseline_comparison} />

          {/* Diagnostics */}
          <DiagnosticsSection diagnostics={modelDescription.evaluation.diagnostics} />

          {/* Explainability */}
          <ExplainabilitySection explainability={modelDescription.evaluation.explainability} />

          {/* Robustness */}
          <RobustnessSection robustness={modelDescription.evaluation.robustness} />

          {/* Recommendations */}
          <RecommendationsSection recommendations={modelDescription.evaluation.recommendations} />
        </div>
      ) : (
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-6 text-center">
          <BarChartIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No evaluation data available for this model.
          </p>
        </div>
      )}
    </div>
  );
}



// Component for rendering API usage section
function ApiUsageSection({ model, modelVersion, modelDescription, generateSampleInput, copyToClipboard, copiedText }) {
  const apiEndpoint = model.status === 'completed' 
    ? `${model?.model_name || 'your-model-name'}/${model?.version || modelVersion}/infer`
    : `${model.model_id}/infer`;
  
  const microbatchEndpoint = model.status === 'completed' 
    ? `${model?.model_name || 'your-model-name'}/${model?.version || modelVersion}/predictions`
    : `${model.model_id}/predictions`;
  
  const description = model.status === 'completed'
    ? "Example of how to use this model in your application."
    : "Example of how to use this model once training is complete.";
    
  const curlExample = `curl -X POST "https://api.plexe.ai/models/${apiEndpoint}" \\
-H "Content-Type: application/json" \\
-H "x-api-key: YOUR_API_KEY" \\
-d '${generateSampleInput()}'`;

  const sampleInput = generateSampleInput();
  const microbatchExample = `curl -X POST "https://api.plexe.ai/models/${microbatchEndpoint}" \\
-H "Content-Type: application/json" \\
-H "x-api-key: YOUR_API_KEY" \\
-d '[
  ${sampleInput},
  ${sampleInput}
]'`;

  const buildModelSpec = () => {
    const modelName = model.model_name || 'your-model-name';
    const modelId = model.version || modelVersion || model.model_id;
    const inputSchema = modelDescription?.input_schema || model?.input_schema;
    const outputSchema = modelDescription?.output_schema || model?.output_schema;
    const sampleRequest = modelDescription?.sample_request || model?.sample_request;

    const spec = buildOpenApiSpec({
      modelName,
      modelId,
      inputSchema: inputSchema || {},
      outputSchema: outputSchema || {},
      sampleRequest,
      apiBaseUrl: 'https://api.plexe.ai',
    });

    return { spec, modelName, modelId };
  };

  const handleDownloadOpenApiSpec = () => {
    try {
      const { spec, modelName, modelId } = buildModelSpec();
      downloadFile(JSON.stringify(spec, null, 2), `openapi-${slugify(modelName)}-${slugify(modelId)}.json`, 'application/json');
    } catch (err) {
      console.error('Failed to generate OpenAPI spec', err);
    }
  };

  const handleDownloadApiPortal = () => {
    try {
      const { spec, modelName, modelId } = buildModelSpec();
      const title = `${modelName} Inference API`;
      const html = buildScalarHtml(spec, title);
      downloadFile(html, `api-portal-${slugify(modelName)}-${slugify(modelId)}.html`, 'text/html');
    } catch (err) {
      console.error('Failed to generate API portal', err);
    }
  };

  return (
    <div className="space-y-8">
      {/* Download Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleDownloadOpenApiSpec}
          className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-slate-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-slate-600 transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] text-sm font-medium"
        >
          <DownloadIcon className="h-4 w-4" />
          Download OpenAPI Spec
        </button>
        <button
          onClick={handleDownloadApiPortal}
          className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-slate-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-slate-600 transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] text-sm font-medium"
        >
          <DownloadIcon className="h-4 w-4" />
          Download API Portal
        </button>
      </div>

      {/* Single Inference */}
      <div>
        <h3 className="text-lg font-medium mb-2">Single Inference</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{description}</p>
        <div className="relative">
          <pre className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-md overflow-auto text-xs font-mono">
            {curlExample}
          </pre>
          <button
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-300"
            onClick={() => copyToClipboard(curlExample)}
          >
            {copiedText === curlExample ? (
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
            ) : (
              <ClipboardIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Microbatching */}
      <div>
        <h3 className="text-lg font-medium mb-2">Microbatching</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Process multiple samples in a single request for improved throughput.
        </p>
        <div className="relative">
          <pre className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-md overflow-auto text-xs font-mono">
            {microbatchExample}
          </pre>
          <button
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-300"
            onClick={() => copyToClipboard(microbatchExample)}
          >
            {copiedText === microbatchExample ? (
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
            ) : (
              <ClipboardIcon className="h-4 w-4" />
            )}
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Response format: <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">[{`{"predictions": ...}`}, {`{"predictions": ...}`}]</code>
        </p>
      </div>

      {/* Batch Inference Jobs */}
      <div>
        <h3 className="text-lg font-medium mb-2">Batch Inference Jobs</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Process entire datasets asynchronously with batch jobs. Submit a job, monitor its progress, and download results when complete.
        </p>

        {/* Create Batch Job */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-2">1. Create a Batch Job</h4>
          <div className="relative">
            <pre className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-md overflow-auto text-xs font-mono">
{`curl -X POST "https://api.plexe.ai/batch-inference-jobs" \\
-H "Content-Type: application/json" \\
-H "x-api-key: YOUR_API_KEY" \\
-d '{
  "model_id": "${model.model_id}",
  "dataset_id": "YOUR_DATASET_ID"
}'`}
            </pre>
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-300"
              onClick={() => copyToClipboard(`curl -X POST "https://api.plexe.ai/batch-inference-jobs" \\
-H "Content-Type: application/json" \\
-H "x-api-key: YOUR_API_KEY" \\
-d '{
  "model_id": "${model.model_id}",
  "dataset_id": "YOUR_DATASET_ID"
}'`)}
            >
              {copiedText === `curl -X POST "https://api.plexe.ai/batch-inference-jobs" \\
-H "Content-Type: application/json" \\
-H "x-api-key: YOUR_API_KEY" \\
-d '{
  "model_id": "${model.model_id}",
  "dataset_id": "YOUR_DATASET_ID"
}'` ? (
                <CheckCircleIcon className="h-4 w-4 text-green-500" />
              ) : (
                <ClipboardIcon className="h-4 w-4" />
              )}
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Response: <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">{`{"job_id": "...", "status": "pending", "created_at": "...", "message": "..."}`}</code>
          </p>
        </div>

        {/* Check Job Status */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-2">2. Check Job Status</h4>
          <div className="relative">
            <pre className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-md overflow-auto text-xs font-mono">
{`curl -X GET "https://api.plexe.ai/batch-inference-jobs/JOB_ID" \\
-H "x-api-key: YOUR_API_KEY"`}
            </pre>
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-300"
              onClick={() => copyToClipboard(`curl -X GET "https://api.plexe.ai/batch-inference-jobs/JOB_ID" \\
-H "x-api-key: YOUR_API_KEY"`)}
            >
              {copiedText === `curl -X GET "https://api.plexe.ai/batch-inference-jobs/JOB_ID" \\
-H "x-api-key: YOUR_API_KEY"` ? (
                <CheckCircleIcon className="h-4 w-4 text-green-500" />
              ) : (
                <ClipboardIcon className="h-4 w-4" />
              )}
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Response: <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">{`{"job_id": "...", "model_id": "...", "input_dataset_id": "...", "status": "...", "created_at": "...", "updated_at": "...", "pre_signed_url": "..." (optional)}`}</code>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Status values: <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">pending</code>, <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">running</code>, <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">completed</code>, <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">failed</code>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Note: <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">pre_signed_url</code> is only included when status is <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">completed</code>
          </p>
        </div>

        {/* List All Jobs */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-2">3. List All Jobs</h4>
          <div className="relative">
            <pre className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-md overflow-auto text-xs font-mono">
{`curl -X GET "https://api.plexe.ai/batch-inference-jobs" \\
-H "x-api-key: YOUR_API_KEY"`}
            </pre>
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-300"
              onClick={() => copyToClipboard(`curl -X GET "https://api.plexe.ai/batch-inference-jobs" \\
-H "x-api-key: YOUR_API_KEY"`)}
            >
              {copiedText === `curl -X GET "https://api.plexe.ai/batch-inference-jobs" \\
-H "x-api-key: YOUR_API_KEY"` ? (
                <CheckCircleIcon className="h-4 w-4 text-green-500" />
              ) : (
                <ClipboardIcon className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Download Results */}
        <div>
          <h4 className="text-sm font-semibold mb-2">4. Download Results</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            When the job status is <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">completed</code>, the response will include a <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">pre_signed_url</code> field to download the results CSV file.
          </p>
          <div className="relative">
            <pre className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-md overflow-auto text-xs font-mono">
{`# Download results using the pre-signed URL from the status response
curl -X GET "PRE_SIGNED_URL_FROM_STATUS" -o results.csv`}
            </pre>
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-300"
              onClick={() => copyToClipboard(`# Download results using the pre-signed URL from the status response
curl -X GET "PRE_SIGNED_URL_FROM_STATUS" -o results.csv`)}
            >
              {copiedText === `# Download results using the pre-signed URL from the status response
curl -X GET "PRE_SIGNED_URL_FROM_STATUS" -o results.csv` ? (
                <CheckCircleIcon className="h-4 w-4 text-green-500" />
              ) : (
                <ClipboardIcon className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Component for rendering batch jobs section
function BatchJobsSection({ model }) {
  const [batchJobs, setBatchJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const { addNotification } = useNotification();

  // Extract model name and version
  const modelName = model.model_name || model.model_id;
  const modelVersion = model.version || model.model_version || model.model_id;

  const fetchBatchJobs = useCallback(async () => {
    try {
      const response = await batchInferenceService.getBatchJobs(modelName, modelVersion);
      setBatchJobs(response || []);
    } catch (error) {
      console.error('Error fetching batch jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [modelName, modelVersion]);

  // Fetch batch jobs on mount and periodically
  useEffect(() => {
    if (modelName && modelVersion) {
      fetchBatchJobs();
      const interval = setInterval(fetchBatchJobs, 10000); // Poll every 10 seconds
      return () => clearInterval(interval);
    }
  }, [modelName, modelVersion, fetchBatchJobs]);

  const handleCreateBatchJob = async () => {
    if (!selectedDataset) {
      addNotification('Please select a dataset', 'error');
      return;
    }

    setCreating(true);
    try {
      await batchInferenceService.createBatchJob(model.model_id, selectedDataset);
      addNotification('Batch job created successfully', 'success');
      setShowCreateForm(false);
      setSelectedDataset('');
      fetchBatchJobs(); // Refresh the list
    } catch (error) {
      console.error('Error creating batch job:', error);
      addNotification(error.response?.data?.message || 'Failed to create batch job', 'error');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-medium">Batch Inference Jobs</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Run inference on entire datasets asynchronously
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
        >
          <PlusIcon className="h-4 w-4 inline-block mr-2" />
          New Batch Job
        </button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
          <h4 className="text-md font-medium mb-3">Create New Batch Job</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Dataset
              </label>
              <DatasetSelector
                selectedDataset={selectedDataset}
                onDatasetChange={setSelectedDataset}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleCreateBatchJob}
                disabled={!selectedDataset || creating}
                className="px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
              >
                {creating ? (
                  <span className="flex items-center">
                    <div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full mr-2"></div>
                    Creating...
                  </span>
                ) : (
                  'Create Job'
                )}
              </button>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setSelectedDataset('');
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-slate-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-slate-600 transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Jobs List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin h-8 w-8 mx-auto border-b-2 border-primary-600 dark:border-primary-500 rounded-full"></div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading batch jobs...</p>
        </div>
      ) : batchJobs.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No batch jobs</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Get started by creating a new batch inference job.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {batchJobs.map((job) => (
            <BatchJobCard key={job.job_id} job={job} modelName={modelName} modelVersion={modelVersion} />
          ))}
        </div>
      )}
    </div>
  );
}

// Batch job card component
function BatchJobCard({ job, modelName, modelVersion }) {
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const fetchDetails = async () => {
    if (details) return; // Already loaded
    
    setLoadingDetails(true);
    try {
      const response = await batchInferenceService.getBatchJobStatus(modelName, modelVersion, job.job_id);
      setDetails(response.data);
    } catch (error) {
      console.error('Error fetching job details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleExpand = () => {
    setExpanded(!expanded);
    if (!expanded && !details) {
      fetchDetails();
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      processing: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.pending}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div className="flex items-center justify-between cursor-pointer" onClick={handleExpand}>
        <div className="flex items-center space-x-4">
          <ChevronRightIcon className={`h-5 w-5 text-gray-400 transform transition-transform ${expanded ? 'rotate-90' : ''}`} />
          <div>
            <div className="flex items-center space-x-3">
              <span className="font-mono text-sm">{job.job_id}</span>
              {getStatusBadge(job.status)}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Created: {new Date(job.created_at).toLocaleString()}
            </p>
          </div>
        </div>
        {job.status === 'completed' && (details?.pre_signed_url || job.pre_signed_url) && (
          <a
            href={details?.pre_signed_url || job.pre_signed_url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
            onClick={(e) => e.stopPropagation()}
          >
            <DownloadIcon className="h-4 w-4 inline-block mr-1" />
            Download Results
          </a>
        )}
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          {loadingDetails ? (
            <div className="text-center py-4">
              <div className="animate-spin h-6 w-6 mx-auto border-b-2 border-primary-600 dark:border-primary-500 rounded-full"></div>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Dataset ID:</span>
                <span className="ml-2 font-mono">{job.input_dataset_id}</span>
              </div>
              {job.error_message && (
                <div>
                  <span className="font-medium text-red-600 dark:text-red-400">Error:</span>
                  <p className="mt-1 text-red-600 dark:text-red-400">{job.error_message}</p>
                </div>
              )}
              {job.status === 'processing' && (
                <div className="flex items-center text-yellow-600 dark:text-yellow-400">
                  <div className="animate-spin h-4 w-4 border-b-2 border-yellow-600 dark:border-yellow-400 rounded-full mr-2"></div>
                  Processing your dataset...
                </div>
              )}
              {job.status === 'completed' && (
                <div className="text-green-600 dark:text-green-400">
                  ✓ Job completed successfully
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Component for rendering model versions section
function VersionsSection({ model }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrainLoading, setRetrainLoading] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState('');
  const [showRetrainForm, setShowRetrainForm] = useState(false);
  const { notify } = useNotification();
  const navigate = useNavigate();

  const fetchVersions = useCallback(async () => {
    try {
      setLoading(true);
      const versionsData = await modelService.getModelVersions(model.model_id);
      setVersions(versionsData || []);
    } catch (error) {
      console.error('Error fetching model versions:', error);
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [model.model_id]);

  useEffect(() => {
    if (model?.model_id) {
      fetchVersions();
    }
  }, [fetchVersions, model]);

  const handleRetrain = async () => {
    if (!selectedDataset) {
      notify.error('Please select a dataset');
      return;
    }

    setRetrainLoading(true);
    try {
      await modelService.retrainModel(model.model_id, [selectedDataset]);
      notify.success('Model retraining started successfully');
      setShowRetrainForm(false);
      setSelectedDataset('');
      
      // Refresh versions after starting retrain
      setTimeout(() => {
        fetchVersions();
      }, 2000);
    } catch (error) {
      console.error('Error retraining model:', error);
      notify.error(error.response?.data?.message || 'Failed to retrain model');
    } finally {
      setRetrainLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      processing: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        statusStyles[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
      }`}>
        {status}
      </span>
    );
  };

  const getVersionType = (version) => {
    if (version.model_description?.model_lineage?.parent_model === model.model_id || 
        version.model_description?.retraining_info?.original_experiment_id === model.model_id) {
      return 'Retrained';
    }
    return 'Original';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <GitBranchIcon className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
          <h2 className="text-xl font-semibold">Model Versions</h2>
        </div>
        {model.status === 'completed' && (
          <button
            onClick={() => setShowRetrainForm(!showRetrainForm)}
            className="px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
          >
            <PlusIcon className="h-4 w-4 inline-block mr-2" />
            Retrain Model
          </button>
        )}
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        All versions of this model including original and retrained versions.
      </p>

      {/* Retrain Form */}
      {showRetrainForm && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
          <h4 className="text-md font-medium mb-3">Create New Model Version</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Dataset
              </label>
              <DatasetSelector
                selectedDataset={selectedDataset}
                onDatasetChange={setSelectedDataset}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleRetrain}
                disabled={!selectedDataset || retrainLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
              >
                {retrainLoading ? (
                  <span className="flex items-center">
                    <div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full mr-2"></div>
                    Creating Version...
                  </span>
                ) : (
                  'Create Version'
                )}
              </button>
              <button
                onClick={() => {
                  setShowRetrainForm(false);
                  setSelectedDataset('');
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-slate-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-slate-600 transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Model Version Tree */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin h-8 w-8 mx-auto border-b-2 border-primary-600 dark:border-primary-500 rounded-full"></div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading versions...</p>
        </div>
      ) : versions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
          <HistoryIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No versions found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            This model doesn't have any versions yet.
          </p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-3 top-6 bottom-0 w-px bg-gray-200 dark:bg-gray-700"></div>
          
          <div className="space-y-4">
            {versions.map((version, index) => {
              const isOriginal = version.model_id === model.model_id;
              const versionType = getVersionType(version);
              
              return (
                <div key={version.model_id} className="relative flex items-start space-x-4">
                  {/* Timeline Node */}
                  <div className={`relative z-10 flex-shrink-0 w-2.5 h-2.5 rounded-full border-2 ${
                    isOriginal 
                      ? 'bg-blue-500 border-white dark:border-slate-800' 
                      : 'bg-green-500 border-white dark:border-slate-800'
                  }`}>
                  </div>
                  
                  {/* Version Card */}
                  <div className="flex-1 min-w-0 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 group">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {version.model_name || `${version.model_id.slice(0, 8)}`}
                        </h3>
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          isOriginal 
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        }`}>
                          {isOriginal ? 'Original' : 'Retrained'}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          version.status === 'completed' 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : version.status === 'failed'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                        }`}>
                          {version.status}
                        </span>
                        
                        <button
                          onClick={() => navigate(`/models/${version.model_id}`)}
                          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                        >
                          <ArrowLeftIcon className="w-4 h-4 rotate-180" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-4">
                        <span>Created {new Date(version.created_at).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric',
                          year: 'numeric'
                        })}</span>
                        {version.updated_at && version.updated_at !== version.created_at && (
                          <span>• Updated {new Date(version.updated_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}</span>
                        )}
                      </div>
                      <code className="text-xs font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        {version.model_id.substring(0, 8)}
                      </code>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Component for rendering collapsible code sections
function CollapsibleCodeSection({ title, code, codeKey, modelId, defaultExpanded = true }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [isEditing, setIsEditing] = useState(false);
  const [editedCode, setEditedCode] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);

  if (!code) return null;

  // Ensure code is a string to prevent SyntaxHighlighter crashes
  const codeString = typeof code === 'string' ? code : JSON.stringify(code, null, 2);
  const displayCode = isEditing ? editedCode : codeString;

  // Map code types to their appropriate phases
  const getPhaseForCodeType = (key) => {
    const lowerKey = key.toLowerCase();

    // Specific code file mappings (check these first)
    if (lowerKey.includes('src_trainer')) {
      return { phase: '04_search_models', phaseName: 'Model Search' };
    }

    if (lowerKey.includes('src_pipeline')) {
      return { phase: '02_prepare_data', phaseName: 'Data Preparation' };
    }

    // Feature engineering / data preparation
    if (lowerKey.includes('feature') || lowerKey.includes('preprocess') || lowerKey.includes('transform') || lowerKey.includes('pipeline')) {
      return { phase: '02_prepare_data', phaseName: 'Data Preparation' };
    }

    // Baseline training code
    if (lowerKey.includes('baseline')) {
      return { phase: '03_build_baselines', phaseName: 'Baseline Models' };
    }

    // Advanced training code (non-baseline)
    if (lowerKey.includes('train') || lowerKey.includes('fit')) {
      return { phase: '04_search_models', phaseName: 'Model Search' };
    }

    // Prediction / inference code (default)
    return { phase: '06_package_final_model', phaseName: 'Model Packaging' };
  };

  const { phase: resumePhase, phaseName: resumePhaseName } = getPhaseForCodeType(codeKey);

  const handleEdit = () => {
    setEditedCode(codeString);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedCode('');
  };

  const handleApply = async () => {
    try {
      setApplyingCode(true);

      // Extract experiment_id from modelId
      const experimentId = modelId.includes('/') ? modelId.split('/')[1] : modelId;

      // Update the checkpoint with the new code at the appropriate phase
      await modelService.updateCheckpoint(experimentId, resumePhase, codeKey, editedCode);

      // Rebuild the model from the appropriate phase
      await modelService.rebuildModel(experimentId, { resume_from_phase: resumePhase });

      // Exit edit mode
      setIsEditing(false);

      alert(`Code updated successfully! Model will rebuild from ${resumePhaseName} phase.`);
    } catch (err) {
      console.error('Error applying code changes:', err);
      alert('Failed to apply code changes. Please try again.');
    } finally {
      setApplyingCode(false);
    }
  };

  return (
    <div className="mb-6 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700/50 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
      >
        <div className="flex items-center">
          <CodeIcon className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
        <div className="flex items-center gap-3">
          {expanded && !isEditing && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEdit();
              }}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors border-b border-blue-400 dark:border-blue-500 hover:border-blue-700 dark:hover:border-blue-300 pb-0.5"
            >
              Edit
            </button>
          )}
          {expanded ? (
            <ChevronDownIcon className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRightIcon className="w-5 h-5 text-gray-500" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="bg-white dark:bg-slate-800">
          {isEditing ? (
            <div className="bg-slate-900">
              <Editor
                height="500px"
                language="python"
                theme="vs-dark"
                value={displayCode}
                onChange={(value) => setEditedCode(value)}
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  tabSize: 4,
                  wordWrap: 'on',
                  wrappingIndent: 'indent',
                }}
              />
              <div className="px-4 py-3 bg-gray-800 border-t border-gray-700 flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  Changes will rebuild from {resumePhaseName} phase
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCancel}
                    disabled={applyingCode}
                    className="
                      px-3 py-1.5
                      text-xs font-medium
                      text-gray-300 hover:text-white
                      transition-colors
                      disabled:opacity-50
                    "
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApply}
                    disabled={applyingCode}
                    className="
                      px-3 py-1.5
                      text-xs font-medium
                      text-white
                      bg-blue-600 hover:bg-blue-700
                      rounded
                      transition-colors
                      disabled:opacity-50 disabled:cursor-not-allowed
                      flex items-center gap-1.5
                    "
                  >
                    {applyingCode ? (
                      <>
                        <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Applying...
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Apply & Rebuild
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4">
              <SyntaxHighlighter
                language="python"
                style={vscDarkPlus}
                customStyle={{
                  margin: 0,
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
                showLineNumbers
              >
                {codeString}
              </SyntaxHighlighter>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Component for rendering model build logs section
function ModelImplementationSection({ modelDescription, modelId }) {
  // Helper to format code key to readable title
  const formatCodeTitle = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .filter(word => word.length > 0)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Dynamically generate code sections from modelDescription.code
  const codeSections = modelDescription?.code
    ? Object.keys(modelDescription.code).map(key => ({
        key: key,
        title: formatCodeTitle(key)
      }))
    : [];

  return (
    <div>
      <div className="flex items-center mb-6">
        <CodeIcon className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
        <h2 className="text-xl font-semibold">Implementation</h2>
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Source code for model training, prediction, and feature processing.
      </p>

      {/* Code Sections - Display if modelDescription.code exists */}
      {codeSections.length > 0 ? (
        <div className="space-y-4">
          {codeSections.map(({ key, title }) => (
            <CollapsibleCodeSection
              key={key}
              title={title}
              code={modelDescription.code[key]}
              codeKey={key}
              modelId={modelId}
              defaultExpanded={false}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-slate-800/50">
          <CodeIcon className="h-12 w-12 mx-auto mb-3 text-gray-400 dark:text-gray-600" />
          <p className="text-gray-500 dark:text-gray-400">No implementation code available for this model.</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Implementation details will appear here once the model build is complete.
          </p>
        </div>
      )}
    </div>
  );
}


function ModelDetails() {
  // Extract model ID from URL
  const { modelId } = useParams();
  let modelVersion = null;
  
  if (modelId) {
    if (modelId.includes('/')) {
      const [, version] = modelId.split('/');
      modelVersion = version;
    } else {
      modelVersion = modelId;
    }
  }
  
  // Use the custom hook for model polling
  const { model, modelDescription, loading, loadingRetryCount, fetchModelDetails } = useModelPolling(modelId);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // Helper functions
  const [copiedText, setCopiedText] = useState(null);
  const [showRetrainModal, setShowRetrainModal] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState('');
  const [retrainLoading, setRetrainLoading] = useState(false);
  const [rebuildLoading, setRebuildLoading] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testInput, setTestInput] = useState('');
  const [testResponse, setTestResponse] = useState(null);
  const [showMongoForm, setShowMongoForm] = useState(false);
  const [mongoConfig, setMongoConfig] = useState({
    connectionString: '',
    database: '',
    collection: '',
    vectorField: 'embedding',
    metadataFields: ''
  });
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState(null);
  const [isBatchMode, setIsBatchMode] = useState(false);
  const { addNotification } = useNotification();

  // Checkpoint viewer state
  const [showCheckpointViewer, setShowCheckpointViewer] = useState(false);
  const [pausedPhase, setPausedPhase] = useState(null);
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 2000);
  };

  // Handle test inference
  const handleTestInference = async () => {
    if (!testInput.trim()) {
      setTestError('Please provide input data');
      return;
    }

    // Validate JSON
    let parsedInput;
    try {
      parsedInput = JSON.parse(testInput);
    } catch (error) {
      setTestError('Invalid JSON format. Please check your input.');
      return;
    }

    // Batch mode validation
    if (isBatchMode) {
      if (!Array.isArray(parsedInput)) {
        setTestError('Batch mode requires an array of samples. Expected format: [{...}, {...}]');
        return;
      }
      if (parsedInput.length === 0) {
        setTestError('Batch array cannot be empty. Please provide at least one sample.');
        return;
      }
      if (parsedInput.length > 8) {
        setTestError('Batch size exceeds maximum limit of 8 samples. Please reduce the number of samples.');
        return;
      }
      // Validate each sample is an object
      for (let i = 0; i < parsedInput.length; i++) {
        if (typeof parsedInput[i] !== 'object' || parsedInput[i] === null || Array.isArray(parsedInput[i])) {
          setTestError(`Sample at index ${i} must be a JSON object, not ${Array.isArray(parsedInput[i]) ? 'an array' : typeof parsedInput[i]}.`);
          return;
        }
      }
    } else {
      // Single mode validation
      if (Array.isArray(parsedInput)) {
        setTestError('Single mode expects a JSON object, not an array. Switch to Batch mode or provide a single object.');
        return;
      }
    }

    setTestLoading(true);
    setTestError(null);
    setTestResponse(null);

    try {
      // Extract model name and version
      let modelName, modelVersion;
      if (model?.model_id?.includes('/')) {
        [modelName, modelVersion] = model.model_id.split('/');
      } else {
        modelName = model.model_name || 'model';
        modelVersion = model.version || model.model_id;
      }

      // Call appropriate API based on mode
      let response;
      if (isBatchMode) {
        response = await modelService.predictBatch(modelName, modelVersion, parsedInput);
      } else {
        response = await modelService.predict(modelName, modelVersion, parsedInput);
      }

      setTestResponse(response);
    } catch (error) {
      console.error('Test inference error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to run model inference';
      setTestError(errorMessage);
      addNotification('Model inference failed', 'error');
    } finally {
      setTestLoading(false);
    }
  };

  // Handle retrain
  const handleRetrain = async () => {
    if (!selectedDataset) {
      addNotification('Please select a dataset', 'error');
      return;
    }

    setRetrainLoading(true);
    try {
      // Create a new model with the same configuration but new dataset
      const retrainData = {
        upload_id: selectedDataset,
        file_ids: [],  // Empty array means use all files in the upload
        intent: model.intent,
        input_schema: model.input_schema || modelDescription?.input_schema,
        output_schema: model.output_schema || modelDescription?.output_schema,
        provider: model.provider || 'openai',
        max_iterations: model.max_iterations || 5,
        timeout: model.timeout || 1800,
        auto_deploy: true,
        model_name: model.model_name,
        auto_infer_schema: false
      };

      // Generate a new model name with timestamp (using hyphens, not underscores)
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').toLowerCase();
      const newModelName = `${model.model_name}-retrain-${timestamp}`;
      retrainData.model_name = newModelName;

      await modelService.retrainModel(model.model_id, [selectedDataset], newModelName);

      addNotification('Model retraining started successfully', 'success');
      setShowRetrainModal(false);
      setSelectedDataset('');

      // Navigate to models page to see the new model
      setTimeout(() => {
        navigate('/models');
      }, 1500);
    } catch (error) {
      console.error('Error retraining model:', error);
      addNotification(error.response?.data?.message || 'Failed to retrain model', 'error');
    } finally {
      setRetrainLoading(false);
    }
  };

  // Handle rebuild
  const handleDownloadModel = async () => {
    try {
      addNotification('Preparing download...', 'info');
      const response = await modelService.downloadModel(model.model_id);
      if (response.artifacts && response.artifacts.length > 0) {
        addNotification(`Downloading ${response.artifacts.length} file(s)...`, 'success');
      } else {
        addNotification('No artifacts available to download', 'warning');
      }
    } catch (error) {
      console.error('Error downloading model:', error);
      addNotification('Failed to download model artifacts', 'error');
    }
  };

  const handleRebuild = async () => {
    if (!window.confirm('Are you sure you want to rebuild this model with the same configuration? This will re-run the training process.')) {
      return;
    }

    setRebuildLoading(true);
    try {
      await modelService.rebuildModel(model.model_id);
      addNotification('Model rebuild started successfully', 'success');

      // Refresh the model details to show updated status
      setTimeout(() => {
        fetchModelDetails();
      }, 2000);
    } catch (error) {
      console.error('Error rebuilding model:', error);
      addNotification(error.response?.data?.message || 'Failed to rebuild model', 'error');
    } finally {
      setRebuildLoading(false);
    }
  };

  // Handle quick approve for paused builds
  const handleQuickApprove = async (enableAutoMode = false) => {
    try {
      // Extract experiment ID - for models it's just the model_id directly
      const experimentId = model?.model_id;

      // Get phase from model or fallback to 01_analyze_data (Phase 1)
      const phase = model?.paused_at_phase || pausedPhase || '01_analyze_data';

      if (!experimentId) {
        addNotification('Missing experiment ID', 'error');
        console.error('handleQuickApprove: model object', model);
        return;
      }

      await modelService.submitFeedback(experimentId, phase, {
        approved: true,
        comments: enableAutoMode ? 'Quick approved with auto-mode enabled' : 'Quick approved via UI',
        requested_changes: [],
        enable_auto_mode: enableAutoMode
      });

      addNotification(
        enableAutoMode
          ? 'Build will continue in auto mode (no more pauses)!'
          : 'Feedback submitted! Build will resume shortly.',
        'success'
      );

      // Refresh model details
      setTimeout(() => {
        fetchModelDetails();
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      addNotification(error.response?.data?.message || 'Failed to submit feedback', 'error');
    }
  };

  // Handle checkpoint feedback submission
  const handleCheckpointFeedback = async (feedback) => {
    try {
      // Extract experiment ID - for models it's just the model_id directly
      const experimentId = model?.model_id;

      // Get phase from model or fallback to 01_analyze_data (Phase 1)
      const phase = model?.paused_at_phase || pausedPhase || '01_analyze_data';

      if (!experimentId) {
        addNotification('Missing experiment ID', 'error');
        console.error('handleCheckpointFeedback: model object', model);
        return;
      }

      await modelService.submitFeedback(experimentId, phase, feedback);

      addNotification(
        feedback.approved ? 'Approved! Build will continue.' : 'Feedback submitted! Phase will be redone.',
        'success'
      );

      setShowCheckpointViewer(false);

      // Refresh model details
      setTimeout(() => {
        fetchModelDetails();
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      addNotification(error.response?.data?.message || 'Failed to submit feedback', 'error');
    }
  };

  // Update pausedPhase when model changes
  useEffect(() => {
    if (model?.status === 'paused_awaiting_feedback' && model?.paused_at_phase) {
      setPausedPhase(model.paused_at_phase);
    }
  }, [model]);

  // Generate a sample input based on the input schema
  const generateSampleInput = () => {
    // Prefer explicit sample_request from the experiment if available
    const sampleRequest = modelDescription?.sample_request || model?.sample_request;
    if (sampleRequest && typeof sampleRequest === 'object' && Object.keys(sampleRequest).length > 0) {
      return JSON.stringify(sampleRequest, null, 2);
    }

    const inputSchema = modelDescription?.input_schema || model?.input_schema;
    if (!inputSchema) return '{}';
    
    const sampleInput = {};
    
    // Handle new simple key-value format
    if (!inputSchema.properties && typeof inputSchema === 'object') {
      Object.entries(inputSchema).forEach(([key, type]) => {
        switch (type) {
          case 'str':
          case 'string':
            sampleInput[key] = 'sample string';
            break;
          case 'int':
          case 'integer':
            sampleInput[key] = 42;
            break;
          case 'float':
          case 'number':
            sampleInput[key] = 3.14;
            break;
          case 'bool':
          case 'boolean':
            sampleInput[key] = true;
            break;
          case 'array':
            sampleInput[key] = [];
            break;
          case 'object':
            sampleInput[key] = {};
            break;
          default:
            sampleInput[key] = null;
            break;
        }
      });
    } 
    // Handle original properties-based format
    else if (inputSchema.properties) {
      const properties = inputSchema.properties;
      Object.keys(properties).forEach(key => {
        const prop = properties[key];
        switch (prop.type) {
          case 'string':
            sampleInput[key] = 'sample string';
            break;
          case 'number':
            sampleInput[key] = 3.14;
            break;
          case 'integer':
            sampleInput[key] = 42;
            break;
          case 'boolean':
            sampleInput[key] = true;
            break;
          case 'array':
            sampleInput[key] = [];
            break;
          case 'object':
            sampleInput[key] = {};
            break;
          default:
            sampleInput[key] = null;
            break;
        }
      });
    }
    
    return JSON.stringify(sampleInput, null, 2);
  };

  // Function to get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'baseline_deployed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'pending':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'paused_awaiting_feedback':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  // Render loading state
  if (loading && loadingRetryCount < 3) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin h-8 w-8 mx-auto border-b-2 border-primary-600 dark:border-primary-500 rounded-full"></div>
        <p className="mt-2 text-sm text-gray-500 dark:text-slate-400">Loading model details...</p>
        <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">Model ID: {modelId} (Attempt {loadingRetryCount+1}/3)</p>
      </div>
    );
  }

  // Render error state if model not found
  if (!model) {
    return (
      <div className="text-center py-12">
        <XCircleIcon className="mx-auto h-12 w-12 text-red-400 dark:text-red-500" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Model not found</h3>
        <div className="mt-6">
          <button
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
            onClick={() => navigate('/models')}
          >
            Return to Models
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center mb-8">
        <button
          className="flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-all duration-200 transform hover:scale-105"
          onClick={() => navigate('/models')}
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Models
        </button>
      </div>

      {/* Model Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-slate-800/50 flex items-center justify-center">
              <ZapIcon className="w-6 h-6 text-primary-600 dark:text-silver-400" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-slate-900 dark:text-white">
                {model.model_name || model.intent || 'Model Details'}
              </h1>
              <div className="flex items-center space-x-3 mt-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(model.status)}`}>
                  {model.status}
                </span>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  Created {new Date(model.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            {/* Primary Action: Test Model */}
            <button
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all duration-200 font-medium transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
              onClick={() => setShowTestModal(true)}
            >
              <ZapIcon className="h-4 w-4 mr-2" />
              Test Model
            </button>

            {/* Share Button (Promoted) */}
            {model.status === 'completed' && (
              <ShareModelButton modelId={modelId} />
            )}

            {/* More Actions Dropdown */}
            {model.status === 'completed' && (
              <DropdownMenu>
                <DropdownItem icon={DownloadIcon} onClick={handleDownloadModel}>
                  Download Model
                </DropdownItem>
                <DropdownItem icon={FlaskConicalIcon} onClick={() => setShowRetrainModal(true)}>
                  Retrain with New Data
                </DropdownItem>
                <DropdownItem icon={RefreshCwIcon} onClick={handleRebuild} disabled={rebuildLoading}>
                  {rebuildLoading ? 'Rebuilding...' : 'Rebuild Model'}
                </DropdownItem>
              </DropdownMenu>
            )}

            {/* Rebuild for failed models */}
            {model.status === 'failed' && (
              <DropdownMenu>
                <DropdownItem icon={RefreshCwIcon} onClick={handleRebuild} disabled={rebuildLoading}>
                  {rebuildLoading ? 'Rebuilding...' : 'Rebuild Model'}
                </DropdownItem>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Build Progress Tracker */}
      <BuildProgress
        modelId={modelId}
        status={model.status}
        errorMessage={model.error_message}
        onRebuild={handleRebuild}
      />

      {/* Pause Banner for Awaiting Feedback */}
      {model.status === 'paused_awaiting_feedback' && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg mb-6 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-500"></div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                Build Paused — Awaiting Review
              </h3>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300 mb-6">
              {model.feedback_prompt || "Phase 1 (Data Preparation) is complete. Please review the progress before continuing to experimentation."}
            </p>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {/* Primary: Review & Provide Feedback */}
              <button
                onClick={() => {
                  const experimentId = model.model_id;
                  const phase = model.paused_at_phase || pausedPhase || '01_analyze_data';
                  if (experimentId) {
                    setShowCheckpointViewer(true);
                  } else {
                    addNotification('Missing experiment ID', 'error');
                  }
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
              >
                Review & Provide Feedback
              </button>

              {/* Quick Approve */}
              <button
                onClick={() => handleQuickApprove(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
              >
                Quick Approve
              </button>

              {/* Complete Build (Auto Mode) */}
              <button
                onClick={() => handleQuickApprove(true)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 hover:border-gray-400 dark:hover:border-gray-500 transition-colors flex items-center gap-2"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Complete Build
              </button>
            </div>

            {/* Helper text */}
            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 space-y-1">
              <div>• <span className="font-medium">Review & Provide Feedback:</span> View checkpoint details, approve or request changes</div>
              <div>• <span className="font-medium">Quick Approve:</span> Continue to next phase (may pause again for review)</div>
              <div>• <span className="font-medium">Complete Build:</span> Enable auto-mode and finish without further pauses</div>
            </div>
          </div>
        </div>
      )}

      {/* Checkpoint Inspector Modal */}
      {showCheckpointViewer && (
        <CheckpointInspector
          modelId={model.model_id}
          phase={model.paused_at_phase || pausedPhase || '01_analyze_data'}
          phaseName={model.paused_at_phase || pausedPhase || '01_analyze_data'}
          onClose={() => setShowCheckpointViewer(false)}
        />
      )}

      {/* Status banner for baseline deployed models */}
      {model.status === 'baseline_deployed' && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-md dark:bg-blue-900/20 dark:border-blue-500">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-grow">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  Baseline Model Ready
                </h3>
                <button
                  onClick={() => fetchModelDetails()}
                  className="ml-2 inline-flex items-center px-2 py-1 border border-blue-300 dark:border-blue-500 shadow-sm text-xs font-medium rounded text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-800"
                >
                  <ArrowPathIcon className="h-3 w-3 mr-1" />
                  Refresh Status
                </button>
              </div>
              <div className="mt-1 text-sm text-blue-700 dark:text-blue-300/90">
                <p>✨ Baseline model deployed - you can start making predictions now!</p>
                <p className="mt-1">The final optimized model is still being trained and will automatically replace this baseline when ready.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status banner for processing models */}
      {(model.status === 'processing' || model.status === 'pending') && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-md dark:bg-blue-900/20 dark:border-blue-500">
          <div className="flex">
            <div className="flex-shrink-0">
              <div className="animate-spin h-5 w-5 border-b-2 border-blue-600 dark:border-blue-400 rounded-full"></div>
            </div>
            <div className="ml-3 flex-grow">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  {model.status === 'processing' ? 'Training in progress' : 'Waiting to start'}
                </h3>
                <button
                  onClick={() => fetchModelDetails()}
                  className="ml-2 inline-flex items-center px-2 py-1 border border-blue-300 dark:border-blue-500 shadow-sm text-xs font-medium rounded text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-800"
                >
                  <ArrowPathIcon className="h-3 w-3 mr-1" />
                  Refresh Status
                </button>
              </div>
              <div className="mt-1 text-sm text-blue-700 dark:text-blue-300/90">
                <p>Your model is being trained. This process may take a few minutes.</p>
                {model.status === 'pending' && (
                  <p className="mt-2 text-amber-700 dark:text-amber-400 font-medium">
                    ⚠️ Due to high traffic, your model build may be queued and could take a few hours to complete.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs for different sections */}
      <div>
        <div className="border-b border-slate-200 dark:border-slate-700 mb-8">
          <div className="flex space-x-8">
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-all duration-200 transform hover:scale-105 ${
                activeTab === 'overview'
                  ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300 dark:hover:border-slate-500'
              }`}
              onClick={() => setActiveTab('overview')}
            >
              <InfoIcon className="w-4 h-4 mr-2" />
              Overview
            </button>
            <button
              className={`mr-8 py-4 border-b-2 font-medium text-sm flex items-center transition-all duration-200 transform hover:scale-105 ${
                activeTab === 'performance'
                  ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
              }`}
              onClick={() => setActiveTab('performance')}
            >
              <BarChartIcon className="w-4 h-4 mr-2" />
              Evaluations
            </button>
            <button
              className={`mr-8 py-4 border-b-2 font-medium text-sm flex items-center transition-all duration-200 transform hover:scale-105 ${
                activeTab === 'usage'
                  ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
              }`}
              onClick={() => setActiveTab('usage')}
            >
              <CodeIcon className="w-4 h-4 mr-2" />
              API Usage & Jobs
            </button>
            <button
              className={`py-4 border-b-2 font-medium text-sm flex items-center transition-all duration-200 transform hover:scale-105 ${
                activeTab === 'versions'
                  ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
              }`}
              onClick={() => setActiveTab('versions')}
            >
              <GitBranchIcon className="w-4 h-4 mr-2" />
              Versions
            </button>
            <button
              className={`py-4 border-b-2 font-medium text-sm flex items-center transition-all duration-200 transform hover:scale-105 ${
                activeTab === 'implementation'
                  ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
              }`}
              onClick={() => setActiveTab('implementation')}
            >
              <CodeIcon className="w-4 h-4 mr-2" />
              Implementation
            </button>
            <button
              className={`py-4 border-b-2 font-medium text-sm flex items-center transition-all duration-200 transform hover:scale-105 ${
                activeTab === 'integrations'
                  ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
              }`}
              onClick={() => setActiveTab('integrations')}
            >
              <DatabaseIcon className="w-4 h-4 mr-2" />
              Integrations
              <span className="ml-2 px-1.5 py-0.5 text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                BETA
              </span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-8">
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Model Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <div>
                  <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 flex items-center">
                    <PackageIcon className="w-4 h-4 mr-1.5 text-slate-400" />
                    Model ID
                  </h3>
                  <p className="font-mono text-sm text-slate-900 dark:text-white">{model.model_id}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 flex items-center">
                    <FlaskConicalIcon className="w-4 h-4 mr-1.5 text-slate-400" />
                    Intent
                  </h3>
                  <p className="text-slate-900 dark:text-white">{modelDescription?.intent || model.intent}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 flex items-center">
                    <ActivityIcon className="w-4 h-4 mr-1.5 text-slate-400" />
                    Status
                  </h3>
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(model.status)}`}>
                      {modelDescription?.state || model.status}
                    </span>
                  </div>
                  {model.error_message && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">{model.error_message}</p>
                  )}
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 flex items-center">
                    <CalendarIcon className="w-4 h-4 mr-1.5 text-slate-400" />
                    Created At
                  </h3>
                  <p className="text-slate-900 dark:text-white">{new Date(modelDescription?.training_date || model.created_at).toLocaleString()}</p>
                </div>
                
                <div className="md:col-span-2">
                  <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 flex items-center">
                    <DatabaseIcon className="w-4 h-4 mr-1.5 text-slate-400" />
                    Data Source
                  </h3>
                  <p className="font-mono text-sm text-slate-900 dark:text-white">
                    {model.upload_id || <span className="text-slate-500 dark:text-slate-500 italic">Not specified</span>}
                  </p>
                </div>
              </div>

              {/* Model Architecture Section - moved from ModelImplementationDetails */}
              {modelDescription && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-lg font-medium mb-4 flex items-center">
                    <CpuIcon className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
                    Model Architecture
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {modelDescription.task_type && (
                      <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Task Type</h4>
                        <p className="text-sm">{modelDescription.task_type}</p>
                      </div>
                    )}
                    {modelDescription.domain && (
                      <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Domain</h4>
                        <p className="text-sm">{modelDescription.domain}</p>
                      </div>
                    )}
                    {modelDescription.framework && (
                      <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Framework</h4>
                        <p className="text-sm">{modelDescription.framework}</p>
                      </div>
                    )}
                    {modelDescription.model_type && (
                      <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Model Type</h4>
                        <p className="text-sm">{modelDescription.model_type}</p>
                      </div>
                    )}
                    {modelDescription.model_size && (
                      <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Model Size</h4>
                        <p className="text-sm">
                          {!isNaN(modelDescription.model_size)
                            ? `${(modelDescription.model_size / 1024 / 1024).toFixed(2)} MB`
                            : '—'
                          }
                        </p>
                      </div>
                    )}
                  </div>

                  {modelDescription.architecture_summary && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Architecture Summary</h4>
                      <p className="text-sm bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">{modelDescription.architecture_summary}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Strengths and Limitations - moved from ModelImplementationDetails */}
              {modelDescription && (modelDescription.strengths || modelDescription.limitations) && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-lg font-medium mb-4">Strengths and Limitations</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {modelDescription.strengths && modelDescription.strengths.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center">
                          <CheckCircleIcon className="w-4 h-4 mr-1 text-green-500" />
                          Strengths
                        </h4>
                        <ul className="space-y-2">
                          {modelDescription.strengths.map((strength, index) => (
                            <li key={index} className="text-sm bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 rounded-lg p-3 flex items-start">
                              <span className="text-green-500 mr-2">•</span>
                              {strength}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {modelDescription.limitations && modelDescription.limitations.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center">
                          <AlertTriangleIcon className="w-4 h-4 mr-1 text-amber-500" />
                          Limitations
                        </h4>
                        <ul className="space-y-2">
                          {modelDescription.limitations.map((limitation, index) => (
                            <li key={index} className="text-sm bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 rounded-lg p-3 flex items-start">
                              <span className="text-amber-500 mr-2">•</span>
                              {limitation}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Model Artifacts Section - moved from Technical Details */}
              {modelDescription?.artifacts && modelDescription.artifacts.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-lg font-medium mb-4 flex items-center">
                    <FilesIcon className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
                    Model Artifacts
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {modelDescription.artifacts.map((artifact, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center">
                          <PackageIcon className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
                          <span className="text-sm font-mono">
                            {typeof artifact === 'string' ? artifact : artifact.name}
                          </span>
                        </div>
                        {typeof artifact === 'object' && artifact.type && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-slate-600 px-2 py-1 rounded">
                            {artifact.type}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Implementation Details - moved from Technical Details */}
              {modelDescription?.implementation_details && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-lg font-medium mb-4 flex items-center">
                    <LayersIcon className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
                    Implementation Details
                  </h3>
                  <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {modelDescription.implementation_details.model_type && (
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Model Type:</span>
                          <p className="text-sm font-medium">{modelDescription.implementation_details.model_type}</p>
                        </div>
                      )}
                      {modelDescription.implementation_details.framework && (
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Framework:</span>
                          <p className="text-sm font-medium">{modelDescription.implementation_details.framework}</p>
                        </div>
                      )}
                      {modelDescription.implementation_details.size && (
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Size:</span>
                          <p className="text-sm font-medium">
                            {!isNaN(parseInt(modelDescription.implementation_details.size))
                              ? `${(parseInt(modelDescription.implementation_details.size) / 1024 / 1024).toFixed(2)} MB`
                              : '—'
                            }
                          </p>
                        </div>
                      )}
                    </div>
                    {modelDescription.implementation_details.artifacts && (
                      <div className="mt-4">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Artifacts:</span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {modelDescription.implementation_details.artifacts.map((artifact, idx) => (
                            <span key={idx} className="text-xs bg-gray-200 dark:bg-slate-600 px-2 py-1 rounded font-mono">
                              {artifact}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'performance' && (
            <PerformanceSection model={model} modelDescription={modelDescription} />
          )}

          {activeTab === 'usage' && (
            <div>
              <div className="flex items-center mb-4">
                <CodeIcon className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
                <h2 className="text-xl font-semibold">API Usage & Batch Jobs</h2>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Schema definitions, code examples, and batch processing options for using this model.
              </p>

              {/* Input/Output Schemas */}
              <div className="mb-8">
                <ModelSchemas
                  model={model}
                  modelDescription={modelDescription}
                  copyToClipboard={copyToClipboard}
                  copiedText={copiedText}
                />
              </div>

              {/* API Usage Examples */}
              <div className="mb-8">
                <ApiUsageSection
                  model={model}
                  modelVersion={modelVersion}
                  modelDescription={modelDescription}
                  generateSampleInput={generateSampleInput}
                  copyToClipboard={copyToClipboard}
                  copiedText={copiedText}
                />
              </div>

              {/* Batch Jobs Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <BatchJobsSection
                  model={model}
                />
              </div>

            </div>
          )}



          {activeTab === 'versions' && (
            <VersionsSection 
              model={model}
            />
          )}

          {activeTab === 'implementation' && (
            <ModelImplementationSection
              modelDescription={modelDescription}
              modelId={modelId}
            />
          )}

          {activeTab === 'integrations' && (
            <div>
              <div className="flex items-center mb-4">
                <DatabaseIcon className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
                <h2 className="text-xl font-semibold">Integrations</h2>
                <span className="ml-3 px-2 py-1 text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                  BETA
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Connect your model outputs to downstream systems for search, recommendations, and analytics
              </p>

              {/* Output Destinations Section */}
              <div>
                <h3 className="text-lg font-medium mb-4">Output Destinations</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Configure where model predictions and embeddings are stored for downstream applications
                </p>

                <div className="grid grid-cols-1 gap-4">
                  {/* MongoDB Integration Card */}
                  <div className="bg-white dark:bg-slate-700 border border-blue-200 dark:border-blue-800 rounded-lg p-6 hover:border-blue-300 dark:hover:border-blue-700 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        {/* MongoDB Icon */}
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
                          <DatabaseIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>

                        {/* Content */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-base font-semibold text-slate-900 dark:text-white">
                              MongoDB
                            </h4>
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                              <SparklesIcon className="w-3 h-3" />
                              Vector Storage
                            </span>
                            <span className="text-xs text-emerald-600 dark:text-emerald-400 px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 rounded-sm">
                              Available
                            </span>
                          </div>

                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                            Store model-generated embeddings in MongoDB with vector search capabilities.
                            Ideal for semantic search, recommendation engines, and RAG pipelines where similarity matching is required.
                          </p>

                          <div className="flex flex-wrap gap-2 mb-4">
                            <span className="text-xs bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                              Embeddings Support
                            </span>
                            <span className="text-xs bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                              Vector Similarity Search
                            </span>
                            <span className="text-xs bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                              Real-time Indexing
                            </span>
                            <span className="text-xs bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                              RAG Pipelines
                            </span>
                          </div>

                          <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-slate-600/50 rounded p-3">
                            <strong>Use Cases:</strong> Product recommendations, semantic search, content discovery, RAG retrieval
                          </div>
                        </div>
                      </div>

                      {/* Configure Button */}
                      <button
                        onClick={() => setShowMongoForm(!showMongoForm)}
                        className="ml-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                      >
                        <ExternalLinkIcon className="w-4 h-4" />
                        {showMongoForm ? 'Hide Configuration' : 'Configure Integration'}
                      </button>
                    </div>
                  </div>

                  {/* MongoDB Configuration Form */}
                  {showMongoForm && (
                    <div className="mt-4 bg-gray-50 dark:bg-slate-600/50 rounded-lg p-6 border border-gray-200 dark:border-slate-600">
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">MongoDB Connection Settings</h4>

                      <div className="space-y-4">
                        {/* Connection String */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Connection String *
                          </label>
                          <input
                            type="text"
                            value={mongoConfig.connectionString}
                            onChange={(e) => setMongoConfig({...mongoConfig, connectionString: e.target.value})}
                            placeholder="mongodb+srv://username:password@cluster.mongodb.net"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm font-mono"
                          />
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Your MongoDB connection URI</p>
                        </div>

                        {/* Database Name */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Database Name *
                            </label>
                            <input
                              type="text"
                              value={mongoConfig.database}
                              onChange={(e) => setMongoConfig({...mongoConfig, database: e.target.value})}
                              placeholder="my_database"
                              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                            />
                          </div>

                          {/* Collection Name */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Collection Name *
                            </label>
                            <input
                              type="text"
                              value={mongoConfig.collection}
                              onChange={(e) => setMongoConfig({...mongoConfig, collection: e.target.value})}
                              placeholder="embeddings"
                              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                            />
                          </div>
                        </div>

                        {/* Vector Field Name */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Vector Field Name
                            </label>
                            <input
                              type="text"
                              value={mongoConfig.vectorField}
                              onChange={(e) => setMongoConfig({...mongoConfig, vectorField: e.target.value})}
                              placeholder="embedding"
                              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                            />
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Field name for storing vector embeddings</p>
                          </div>

                          {/* Metadata Fields */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Metadata Fields
                            </label>
                            <input
                              type="text"
                              value={mongoConfig.metadataFields}
                              onChange={(e) => setMongoConfig({...mongoConfig, metadataFields: e.target.value})}
                              placeholder="id, text, timestamp"
                              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                            />
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Comma-separated additional fields</p>
                          </div>
                        </div>

                        {/* Info Box */}
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                          <div className="flex items-start">
                            <InfoIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-blue-800 dark:text-blue-300">
                              <p className="font-medium mb-1">Automatic Vector Indexing</p>
                              <p className="text-xs">
                                Once configured, model outputs will be automatically stored with vector indexes for similarity search.
                                Ensure your MongoDB cluster supports vector search (MongoDB Atlas 6.0.11+).
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-slate-600">
                          <button
                            onClick={() => setShowMongoForm(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              // For demo purposes, just show success notification
                              addNotification('MongoDB integration configured successfully!', 'success');
                              setShowMongoForm(false);
                            }}
                            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            Save Configuration
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Test Inference Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center p-4 z-50" style={{ paddingLeft: '200px' }}>
          <div className="w-full flex justify-center">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium flex items-center">
                      <ZapIcon className="h-5 w-5 mr-2 text-primary-600 dark:text-primary-500" />
                      Test Model Inference
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Test your model's inference endpoint with custom input data.
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Single/Batch Toggle */}
                    <div className="flex items-center gap-2 bg-gray-100 dark:bg-slate-700 rounded-lg p-1">
                      <button
                        onClick={() => {
                          setIsBatchMode(false);
                          setTestInput(generateSampleInput());
                          setTestError(null);
                        }}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                          !isBatchMode
                            ? 'bg-white dark:bg-slate-600 text-primary-600 dark:text-primary-400 shadow-sm'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                      >
                        Single
                      </button>
                      <button
                        onClick={() => {
                          setIsBatchMode(true);
                          setTestInput(JSON.stringify([JSON.parse(generateSampleInput()), JSON.parse(generateSampleInput())], null, 2));
                          setTestError(null);
                        }}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                          isBatchMode
                            ? 'bg-white dark:bg-slate-600 text-primary-600 dark:text-primary-400 shadow-sm'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                      >
                        Batch
                      </button>
                    </div>
                    <button
                      onClick={() => setShowTestModal(false)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
                  {/* Input Panel */}
                  <div className="p-6 border-r border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        Input Data {isBatchMode && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(Max 8 samples)</span>}
                      </h4>
                      <button
                        onClick={() => {
                          if (isBatchMode) {
                            // Generate array of 2 sample inputs for batch mode
                            const sampleObj = JSON.parse(generateSampleInput());
                            setTestInput(JSON.stringify([sampleObj, sampleObj], null, 2));
                          } else {
                            // Generate single sample input
                            setTestInput(generateSampleInput());
                          }
                          setTestError(null);
                        }}
                        className="text-xs text-primary-600 dark:text-primary-500 hover:text-primary-700 dark:hover:text-primary-400 transition-all duration-200 hover:scale-105"
                      >
                        Reset to Sample
                      </button>
                    </div>
                    <div className="border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
                      <Editor
                        height="320px"
                        language="json"
                        theme="vs-dark"
                        value={testInput}
                        onChange={(value) => {
                          setTestInput(value);
                          setTestError(null);
                        }}
                        options={{
                          minimap: { enabled: false },
                          fontSize: 13,
                          lineNumbers: 'on',
                          scrollBeyondLastLine: false,
                          automaticLayout: true,
                          tabSize: 2,
                          wordWrap: 'on',
                          wrappingIndent: 'indent',
                          formatOnPaste: true,
                          formatOnType: true,
                        }}
                      />
                    </div>
                    
                    {/* Error Display */}
                    {testError && (
                      <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                        <div className="flex">
                          <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
                            <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                              <p>{testError}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="mt-6 flex gap-3">
                      <button
                        onClick={handleTestInference}
                        disabled={testLoading || !testInput.trim()}
                        className="px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
                      >
                        {testLoading ? (
                          <>
                            <div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full mr-2"></div>
                            Sending Request...
                          </>
                        ) : (
                          <>
                            <ZapIcon className="h-4 w-4 mr-2" />
                            Send Request
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setShowTestModal(false)}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-slate-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-slate-600 transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>

                  {/* Response Panel */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">Response</h4>
                      {testResponse && isBatchMode && Array.isArray(testResponse) && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {testResponse.length} {testResponse.length === 1 ? 'result' : 'results'}
                        </span>
                      )}
                    </div>
                    <div className="h-80 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-md overflow-auto">
                      {testLoading ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="animate-spin h-8 w-8 border-b-2 border-primary-600 dark:border-primary-500 rounded-full mx-auto mb-2"></div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Running inference...</p>
                          </div>
                        </div>
                      ) : testResponse ? (
                        isBatchMode && Array.isArray(testResponse) ? (
                          <div className="p-4 space-y-3">
                            {testResponse.map((item, idx) => (
                              <div key={idx} className="border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden">
                                <div className="bg-gray-100 dark:bg-slate-700 px-3 py-1.5 border-b border-gray-200 dark:border-gray-600">
                                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Sample {idx + 1}
                                  </span>
                                </div>
                                <pre className="p-3 text-xs text-gray-900 dark:text-gray-100 whitespace-pre-wrap font-mono bg-white dark:bg-slate-800">
                                  {JSON.stringify(item, null, 2)}
                                </pre>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <pre className="p-4 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap font-mono">
                            {JSON.stringify(testResponse, null, 2)}
                          </pre>
                        )
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <ZapIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                            <p className="text-sm text-gray-500 dark:text-gray-400">Response will appear here</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Send a request to see the model's output</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Copy Response Button */}
                    {testResponse && (
                      <div className="mt-4">
                        <button
                          onClick={() => copyToClipboard(JSON.stringify(testResponse, null, 2))}
                          className="text-sm text-primary-600 dark:text-primary-500 hover:text-primary-700 dark:hover:text-primary-400 flex items-center transition-all duration-200 hover:scale-105"
                        >
                          {copiedText === JSON.stringify(testResponse, null, 2) ? (
                            <CheckCircleIcon className="h-4 w-4 mr-1 text-green-500" />
                          ) : (
                            <ClipboardIcon className="h-4 w-4 mr-1" />
                          )}
                          {copiedText === JSON.stringify(testResponse, null, 2) ? 'Copied!' : 'Copy Response'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Retrain Modal */}
      {showRetrainModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium mb-4">Retrain Model</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Select a new dataset to retrain your model. The model will keep the same intent and schema configurations.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Dataset
              </label>
              <DatasetSelector
                selectedDataset={selectedDataset}
                onDatasetChange={setSelectedDataset}
              />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                The new model will be named: {model.model_name}-retrain-[timestamp]
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-slate-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-slate-600"
                onClick={() => {
                  setShowRetrainModal(false);
                  setSelectedDataset('');
                }}
                disabled={retrainLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
                onClick={handleRetrain}
                disabled={!selectedDataset || retrainLoading}
              >
                {retrainLoading ? (
                  <span className="flex items-center">
                    <div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full mr-2"></div>
                    Retraining...
                  </span>
                ) : (
                  'Start Retraining'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Dataset selector component
function DatasetSelector({ selectedDataset, onDatasetChange }) {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDatasets = async () => {
      try {
        const response = await uploadService.getUploads();
        setDatasets(response.data || response || []);
      } catch (error) {
        console.error('Error fetching datasets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDatasets();
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
      </div>
    );
  }

  const getDatasetDisplayName = (dataset) => {
    // First priority: dataset name
    if (dataset.dataset_name) {
      return dataset.dataset_name;
    }
    
    // Second priority: filenames
    if (dataset.files && dataset.files.length > 0) {
      const filenames = dataset.files.map(f => f.filename).join(', ');
      return filenames.length > 50 ? filenames.substring(0, 47) + '...' : filenames;
    }
    
    return dataset.file_name || dataset.upload_id;
  };

  return (
    <select
      value={selectedDataset}
      onChange={(e) => onDatasetChange(e.target.value)}
      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-slate-700 dark:border-gray-600 dark:text-white"
    >
      <option value="">Select a dataset</option>
      {datasets.map((dataset) => (
        <option key={dataset.upload_id} value={dataset.upload_id}>
          {getDatasetDisplayName(dataset)}
        </option>
      ))}
    </select>
  );
}

export default ModelDetails;